import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { 
  BookOpen, 
  ArrowLeft, 
  Mail, 
  MessageCircle, 
  HelpCircle, 
  Clock,
  Phone,
  Facebook,
  Twitter,
  Youtube,
  Linkedin,
  ChevronRight,
  Search,
  Book,
  CreditCard,
  Users,
  Award,
  Download,
  Gift,
  CheckCircle
} from 'lucide-react';

const Support = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    category: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Lead magnet form state
  const [leadForm, setLeadForm] = useState({ name: '', email: '' });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSuccess, setLeadSuccess] = useState(false);

  const handleInputChange = (field, value) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    
    if (!leadForm.name || !leadForm.email) {
      toast.error('Please fill in your name and email');
      return;
    }

    setLeadSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lead-magnet/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: leadForm.name,
          email: leadForm.email
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to subscribe');
      }
      
      setLeadSuccess(true);
      toast.success('Check your email! Your free guide is on its way.');
    } catch (error) {
      console.error('Lead magnet error:', error);
      toast.error('Failed to send guide. Please try again.');
    } finally {
      setLeadSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: contactForm.name,
          email: contactForm.email,
          category: contactForm.category || 'Other',
          subject: contactForm.subject,
          message: contactForm.message
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      
      toast.success('Your message has been sent! We\'ll get back to you within 24 hours.');
      setContactForm({ name: '', email: '', subject: '', category: '', message: '' });
    } catch (error) {
      console.error('Support message error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const faqs = [
    {
      category: "Course Information",
      icon: Book,
      questions: [
        {
          q: "What's the difference between Foundation and Pro courses?",
          a: "The Foundation course covers 6 essential modules including AI review creation, video production, and monetization basics. The Pro course includes everything in Foundation plus 5 advanced modules: brand building masterclass, client chatbot training funnel creation, landing page and ecommerce website, automated client systems, agency scaling strategies, and 6 video testimonials."
        },
        {
          q: "Do I need to read books to create reviews?",
          a: "No! Our course teaches you how to create authentic, engaging book reviews using AI tools without reading the entire book. This is one of our key methodologies."
        },
        {
          q: "How long does it take to complete the course?",
          a: "Courses run for 6 weeks with approximately 5-8 hours of content per week. You can work at your own pace, but we recommend following the weekly schedule for best results."
        }
      ]
    },
    {
      category: "Enrollment & Payments",
      icon: CreditCard,
      questions: [
        {
          q: "Can I pay the enrollment fee first and course fee later?",
          a: "Yes! You can pay the £9.99 enrollment fee to secure your spot, then return later to select and pay for your preferred course level."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept payments through Revolut, which includes credit cards, debit cards, and digital wallets. All transactions are processed securely and encrypted. Payments will appear on your statement as Voliciti Group Ventures Ltd."
        },
        {
          q: "What is your refund policy?",
          a: "All sales are final. Due to the digital nature of our courses and immediate access to valuable content, we do not offer refunds once course materials have been accessed or downloaded. Please review course descriptions carefully before purchasing."
        }
      ]
    },
    {
      category: "Technical Support",
      icon: Users,
      questions: [
        {
          q: "I'm having trouble accessing my course materials",
          a: "Please check your email for login credentials. If you still can't access your materials, contact our support team with your enrollment details."
        },
        {
          q: "Can I download course materials?",
          a: "Yes, most course materials can be downloaded for offline access. Some interactive elements require an internet connection."
        },
        {
          q: "What if I miss a live session?",
          a: "All live sessions are recorded and made available to enrolled students within 24 hours. You'll receive an email notification when recordings are ready."
        }
      ]
    },
    {
      category: "Certification",
      icon: Award,
      questions: [
        {
          q: "How do I get my certification?",
          a: "Complete all required modules and assignments. Your digital certificate will be automatically generated and emailed to you within 48 hours of completion."
        },
        {
          q: "Is the certification recognized in the industry?",
          a: "Our certification is recognized by employers and clients in the digital content and book review industry. Many graduates use it to demonstrate their expertise."
        },
        {
          q: "Can I verify my certification?",
          a: "Yes, all certifications can be verified through our support team. We maintain records of all issued certificates with unique verification codes."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center text-blue-600 hover:text-blue-700">
              <img 
                src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/inexx1ig_IMG-20250713-WA0004%281%29.jpg"
                alt="Book Review Specialists Logo"
                className="w-6 h-6 rounded-full object-cover mr-2"
              />
              <span className="font-semibold">Book Review Specialists</span>
            </Link>
            <Link to="/" className="btn btn-secondary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <HelpCircle className="w-16 h-16 mx-auto mb-6 opacity-90" />
          <h1 className="text-4xl font-bold mb-4">How Can We Help You?</h1>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            We're here to support your journey to becoming a certified video book review specialist.
          </p>
          
          {/* Quick Actions */}
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="p-6 bg-white/10 backdrop-blur-sm border-white/20 text-center">
              <Mail className="w-8 h-8 mx-auto mb-3 text-blue-200" />
              <h3 className="font-semibold mb-2">Email Support</h3>
              <p className="text-sm text-blue-100 mb-4">Get detailed help via email</p>
              <a href="mailto:info@virtualautochat.uk" className="text-blue-200 hover:text-white transition-colors">
                info@virtualautochat.uk
              </a>
            </Card>

            <Card className="p-6 bg-white/10 backdrop-blur-sm border-white/20 text-center">
              <MessageCircle className="w-8 h-8 mx-auto mb-3 text-blue-200" />
              <h3 className="font-semibold mb-2">Live Chat</h3>
              <p className="text-sm text-blue-100 mb-4">Instant support during business hours</p>
              <p className="text-blue-200">Mon-Fri, 9 AM - 5 PM GMT</p>
            </Card>

            <Card className="p-6 bg-white/10 backdrop-blur-sm border-white/20 text-center">
              <Facebook className="w-8 h-8 mx-auto mb-3 text-blue-200" />
              <h3 className="font-semibold mb-2">Community</h3>
              <p className="text-sm text-blue-100 mb-4">Join our Facebook group</p>
              <a 
                href="https://www.facebook.com/groups/449766771396718/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-200 hover:text-white transition-colors"
              >
                Community Group
              </a>
            </Card>
          </div>
        </div>
      </div>

      {/* Free Guide Lead Magnet Section */}
      <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Left side - Image */}
              <div className="order-2 lg:order-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_4d67f842-afde-4e27-ad29-d8a3aca61396/artifacts/81vacddd_IMG_20260401_203709.jpg"
                  alt="Mini Guide - Find Buyers Not Just Followers"
                  className="w-full rounded-xl shadow-2xl"
                />
              </div>
              
              {/* Right side - Form */}
              <div className="order-1 lg:order-2">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                  <div className="flex items-center mb-4">
                    <Gift className="w-8 h-8 text-yellow-400 mr-3" />
                    <span className="text-yellow-400 font-semibold text-lg">FREE GUIDE</span>
                  </div>
                  
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                    How to Profile, Find Buyers & Convert Followers into Serious Buyers
                  </h2>
                  
                  <p className="text-blue-100 mb-6">
                    Learn why most people focus on followers instead of buyers — and get practical ChatGPT prompts to identify high-intent buyers.
                  </p>
                  
                  {!leadSuccess ? (
                    <form onSubmit={handleLeadSubmit} className="space-y-4">
                      <div>
                        <Input
                          type="text"
                          placeholder="Your Name"
                          value={leadForm.name}
                          onChange={(e) => setLeadForm(prev => ({ ...prev, name: e.target.value }))}
                          className="bg-white/90 border-0 text-slate-800 placeholder:text-slate-500"
                          disabled={leadSubmitting}
                        />
                      </div>
                      <div>
                        <Input
                          type="email"
                          placeholder="Your Email"
                          value={leadForm.email}
                          onChange={(e) => setLeadForm(prev => ({ ...prev, email: e.target.value }))}
                          className="bg-white/90 border-0 text-slate-800 placeholder:text-slate-500"
                          disabled={leadSubmitting}
                        />
                      </div>
                      <Button 
                        type="submit" 
                        disabled={leadSubmitting}
                        className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold py-3 text-lg"
                      >
                        {leadSubmitting ? (
                          <div className="flex items-center justify-center">
                            <div className="spinner mr-2"></div>
                            Sending...
                          </div>
                        ) : (
                          <>
                            <Download className="w-5 h-5 mr-2" />
                            Get Free Guide Now
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-blue-200 text-center mt-3">
                        We respect your privacy. Unsubscribe anytime.
                      </p>
                    </form>
                  ) : (
                    <div className="text-center py-6">
                      <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">Check Your Email!</h3>
                      <p className="text-blue-100">
                        Your free guide is on its way to <strong>{leadForm.email}</strong>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {/* FAQ Section */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-800 mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-slate-600">Find quick answers to common questions</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {faqs.map((category, categoryIndex) => {
                const Icon = category.icon;
                return (
                  <Card key={categoryIndex} className="card">
                    <div className="flex items-center mb-6">
                      <Icon className="w-6 h-6 text-blue-600 mr-3" />
                      <h3 className="text-xl font-semibold text-slate-800">{category.category}</h3>
                    </div>
                    
                    <div className="space-y-4">
                      {category.questions.map((faq, faqIndex) => (
                        <div key={faqIndex} className="border-b border-slate-200 pb-4 last:border-b-0">
                          <h4 className="font-medium text-slate-800 mb-2">{faq.q}</h4>
                          <p className="text-slate-600 text-sm leading-relaxed">{faq.a}</p>
                        </div>
                      ))}
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Certificate Sample */}
          <Card className="mb-12 p-6">
            <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center">
              <Award className="w-6 h-6 text-blue-600 mr-2" />
              Certificate Preview
            </h2>
            <p className="text-slate-600 mb-6">
              Here's what your professional certificate will look like when you complete the course:
            </p>
            
            {/* Certificate Design */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-lg border-2 border-blue-200">
              <div className="bg-white p-8 rounded-lg shadow-lg max-w-4xl mx-auto" style={{ aspectRatio: '11.7/8.3' }}>
                {/* Certificate Header */}
                <div className="text-center mb-6">
                  <div className="flex justify-center items-center mb-3">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_edu-signups/artifacts/bupoexyc_book%20review%20specialist%20small%20logo.jpg"
                      alt="Book Review Specialist Logo"
                      className="h-12 w-auto mr-3"
                    />
                    <div className="text-left">
                      <h3 className="text-xl font-bold text-slate-800">Book Review Specialists</h3>
                      <p className="text-sm text-slate-600">Professional Certification Program</p>
                    </div>
                  </div>
                </div>

                {/* Certificate Title */}
                <div className="text-center mb-6">
                  <h4 className="text-3xl font-bold text-blue-700 mb-2">Certificate of Completion</h4>
                  <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 mx-auto mb-3"></div>
                  <p className="text-base text-slate-600">This is to certify that</p>
                </div>

                {/* Student Name */}
                <div className="text-center mb-6">
                  <h5 className="text-2xl font-bold text-slate-800 border-b-2 border-slate-300 pb-2 inline-block min-w-80">
                    [Your Name Here]
                  </h5>
                </div>

                {/* Course Details */}
                <div className="text-center mb-6">
                  <p className="text-base text-slate-700 mb-2">has successfully completed the</p>
                  <h6 className="text-xl font-bold text-blue-700 mb-3">
                    Professional Video Book Review Specialist Course
                  </h6>
                  <p className="text-sm text-slate-600">and is now qualified to operate as a certified video book review specialist</p>
                </div>

                {/* Certificate Details */}
                <div className="grid grid-cols-3 gap-4 mb-6 text-center text-sm">
                  <div>
                    <p className="font-semibold text-slate-700">Certificate ID</p>
                    <p className="font-mono text-base font-bold text-slate-800 bg-slate-100 px-2 py-1 rounded">
                      [Your Referral Code]
                    </p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-700">Completion Date</p>
                    <p className="text-base font-bold text-slate-800">[Date]</p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-700">CPD Provider</p>
                    <p className="text-base font-bold text-slate-800 blur-sm select-none">#778495</p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-between items-end text-xs">
                  <div className="text-left">
                    <div className="w-32 border-b border-slate-400 mb-1"></div>
                    <p className="text-slate-600">
                      <strong>Diane Shawe</strong><br />
                      Founder & Director
                    </p>
                  </div>
                  <div className="text-center">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_edu-signups/artifacts/whbb2v24_Cpd%20ACCREDITED_video%20book%20review%20course.gif"
                      alt="CPD Accredited"
                      className="w-12 h-12 mx-auto mb-1"
                    />
                    <p className="text-slate-600">CPD Accredited</p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-500">Verify at:</p>
                    <p className="text-slate-600 font-semibold">bookreviewspecialists.app</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-4 text-center">
              <p className="text-sm text-slate-600">
                <strong>Certificate Features:</strong> Professional design • Your unique referral code as Certificate ID • 
                CPD Provider #778495 credentials • Downloadable PDF • Admin activation required
              </p>
            </div>
          </Card>

          {/* Contact Form */}
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-slate-800 mb-6">Send Us a Message</h2>
              
              <Card className="card">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        value={contactForm.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Enter your full name"
                        className="mt-1"
                        disabled={submitting}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="Enter your email"
                        className="mt-1"
                        disabled={submitting}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="category">Category</Label>
                    <select
                      id="category"
                      value={contactForm.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      className="form-select mt-1"
                      disabled={submitting}
                    >
                      <option value="">Select a category</option>
                      <option value="course">Course Information</option>
                      <option value="enrollment">Enrollment & Payments</option>
                      <option value="technical">Technical Support</option>
                      <option value="certification">Certification</option>
                      <option value="competition">Competition</option>
                      <option value="claim-discount">Claim Discount</option>
                      <option value="video-book-review">Video Book Review</option>
                      <option value="complaint">Complaint</option>
                      <option value="work-with-us">Work With Us</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      value={contactForm.subject}
                      onChange={(e) => handleInputChange('subject', e.target.value)}
                      placeholder="Brief description of your question"
                      className="mt-1"
                      disabled={submitting}
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      value={contactForm.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder="Please provide details about your question or issue..."
                      rows={6}
                      className="mt-1"
                      disabled={submitting}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="w-full btn btn-primary"
                  >
                    {submitting ? (
                      <div className="flex items-center">
                        <div className="spinner mr-2"></div>
                        Sending...
                      </div>
                    ) : (
                      <>
                        Send Message
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>
              </Card>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-bold text-slate-800 mb-6">Get In Touch</h2>
              
              <div className="space-y-6">
                <Card className="card">
                  <div className="flex items-start">
                    <Mail className="w-6 h-6 text-blue-600 mt-1 mr-4" />
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1">Email Support</h3>
                      <p className="text-slate-600 mb-2">For detailed inquiries and technical support</p>
                      <a href="mailto:info@virtualautochat.uk" className="text-blue-600 hover:text-blue-700">
                        info@virtualautochat.uk
                      </a>
                    </div>
                  </div>
                </Card>

                <Card className="card">
                  <div className="flex items-start">
                    <Clock className="w-6 h-6 text-green-600 mt-1 mr-4" />
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1">Business Hours</h3>
                      <p className="text-slate-600 mb-2">When you can expect responses</p>
                      <div className="text-sm space-y-1">
                        <p><strong>Monday - Friday:</strong> 9:00 AM - 5:00 PM GMT</p>
                        <p><strong>Weekend:</strong> Limited support (emergency only)</p>
                        <p><strong>Response time:</strong> Within 24 hours</p>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="card">
                  <div className="flex items-start">
                    <Users className="w-6 h-6 text-purple-600 mt-1 mr-4" />
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1">Student Community</h3>
                      <p className="text-slate-600 mb-3">Connect with fellow students and get peer support</p>
                      <a 
                        href="https://www.facebook.com/groups/449766771396718/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-700"
                      >
                        <Facebook className="w-4 h-4 mr-2" />
                        Join Facebook Group
                      </a>
                    </div>
                  </div>
                </Card>

                {/* Social Links */}
                <Card className="card">
                  <h3 className="font-semibold text-slate-800 mb-4">Follow Us</h3>
                  <div className="flex space-x-4">
                    <a 
                      href="https://www.facebook.com/groups/449766771396718/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-slate-600 hover:text-blue-600 transition-colors"
                      title="Join our Facebook Group"
                    >
                      <Facebook className="w-6 h-6" />
                    </a>
                    <a 
                      href="https://twitter.com/dianemshawe" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-slate-600 hover:text-blue-400 transition-colors"
                      title="Follow @dianemshawe on X (Twitter)"
                    >
                      <Twitter className="w-6 h-6" />
                    </a>
                    <a 
                      href="https://youtube.com/@dianeshaweauthor?si=hMk5IN5rYPkABOOi" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-slate-600 hover:text-red-600 transition-colors"
                      title="Subscribe to Diane Shawe Author YouTube Channel"
                    >
                      <Youtube className="w-6 h-6" />
                    </a>
                  </div>
                </Card>
              </div>
            </div>
          </div>

          {/* Additional Resources */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-slate-800 mb-8 text-center">Additional Resources</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link to="/privacy" className="group">
                <Card className="card text-center hover:shadow-lg transition-shadow">
                  <HelpCircle className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-800 mb-2 group-hover:text-blue-600">Privacy Policy</h3>
                  <p className="text-sm text-slate-600">How we protect your data</p>
                </Card>
              </Link>

              <Link to="/terms" className="group">
                <Card className="card text-center hover:shadow-lg transition-shadow">
                  <CreditCard className="w-8 h-8 text-green-600 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-800 mb-2 group-hover:text-green-600">Terms of Sale</h3>
                  <p className="text-sm text-slate-600">Purchase terms & refunds</p>
                </Card>
              </Link>

              <Link to="/enroll" className="group">
                <Card className="card text-center hover:shadow-lg transition-shadow">
                  <Book className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-800 mb-2 group-hover:text-purple-600">Enroll Now</h3>
                  <p className="text-sm text-slate-600">Start your journey today</p>
                </Card>
              </Link>

              <Link to="/admin/login" className="group">
                <Card className="card text-center hover:shadow-lg transition-shadow">
                  <Users className="w-8 h-8 text-orange-600 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-800 mb-2 group-hover:text-orange-600">Admin Portal</h3>
                  <p className="text-sm text-slate-600">Administrator access</p>
                </Card>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;